const RAW_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? 'https://supportree-api.rhksdn4446.workers.dev';
export const API_BASE = RAW_BASE.replace(/\/$/, '');

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, msg: string) {
    super(msg);
    this.status = status;
    this.body = body;
  }
}

export function getToken(): string | null {
  return localStorage.getItem('supportree_token');
}
export function setToken(t: string | null) {
  if (t) localStorage.setItem('supportree_token', t);
  else localStorage.removeItem('supportree_token');
}

// 현재 로그인 사용자 정보 (JWT 페이로드 파싱). 서버 재조회 없이 UI 뱃지/자기계정 판별용.
// 서명 검증은 서버에서만 — 여기 값은 표시용도이며 권한 결정에 사용 금지.
export function getCurrentUser(): { id: number; email: string; role: 'admin'|'staff' } | null {
  const t = getToken();
  if (!t) return null;
  const parts = t.split('.');
  if (parts.length !== 3) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? b64 : b64 + '='.repeat(4 - (b64.length % 4));
    const json = decodeURIComponent(escape(atob(pad)));
    const p = JSON.parse(json);
    if (typeof p?.sub !== 'number') return null;
    return { id: p.sub, email: String(p.email ?? ''), role: p.role === 'admin' ? 'admin' : 'staff' };
  } catch { return null; }
}

export async function api<T = unknown>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {};
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  // FormData면 Content-Type을 지정하지 않는다 — 브라우저가 boundary와 함께 자동 설정.
  if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
  const tok = getToken();
  if (tok) headers['Authorization'] = `Bearer ${tok}`;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : (isForm ? (body as FormData) : JSON.stringify(body)),
    cache: 'no-store',
  });
  const text = await res.text();
  let parsed: unknown = null;
  try { parsed = text ? JSON.parse(text) : null; } catch { parsed = text; }
  if (!res.ok) {
    if (res.status === 401) {
      setToken(null);
      // 로그인 페이지가 아닌 경우 강제 이동
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    const errMsg = (parsed && typeof parsed === 'object' && 'error' in parsed) ? String((parsed as { error: unknown }).error) : `HTTP ${res.status}`;
    throw new ApiError(res.status, parsed, errMsg);
  }
  return parsed as T;
}

export const fmt = {
  won(n: number | null | undefined): string {
    if (n == null || isNaN(Number(n))) return '-';
    return Number(n).toLocaleString('ko-KR');
  },
  date(s: string | null | undefined): string {
    if (!s) return '-';
    return s.slice(0, 10);
  },
  pct(n: number | null | undefined): string {
    if (n == null) return '-';
    return (Number(n) * 100).toFixed(2) + '%';
  },
};

export type AuthUser = {
  id: number;
  username: string;
  email: string | null;
  name: string | null;
  position: string | null;
  role: 'admin' | 'staff';
  active: number;
  must_change_password: number;
  last_login_at: string | null;
  created_at: string;
};
